/**
 * Client-side form rules, as zod schemas.
 *
 * These exist to give instant feedback, NOT to decide anything. The server
 * validates the same fields again and its 422 is the authority — every form
 * that uses these also has to render `ApiError.fieldErrors`, because the client
 * cannot know about a pincode we stopped serving this morning.
 *
 * The messages are written for a customer standing in a queue on a phone: they
 * say what a correct value looks like, not which rule failed.
 */
import { z } from 'zod';

/** Indian mobile: ten digits, first one 6-9. Matches the backend's own rule. */
export const PHONE_PATTERN = /^[6-9]\d{9}$/;

/** Indian PIN code: six digits, first not zero. Matches the backend's regex. */
export const PINCODE_PATTERN = /^[1-9]\d{5}$/;

/** Strips spaces, dashes and a +91 / 0 prefix so a pasted number still validates. */
export function normalisePhone(input: string): string {
  const digitsOnly = input.replace(/[^\d]/g, '');
  if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) return digitsOnly.slice(2);
  if (digitsOnly.length === 11 && digitsOnly.startsWith('0')) return digitsOnly.slice(1);
  return digitsOnly;
}

/** Keeps only digits, capped at six. For the pincode input's onChange. */
export function normalisePincode(input: string): string {
  return input.replace(/[^\d]/g, '').slice(0, 6);
}

export const phoneSchema = z
  .string()
  .trim()
  .transform(normalisePhone)
  .pipe(
    z.string().regex(PHONE_PATTERN, 'Enter a 10-digit mobile number starting with 6, 7, 8 or 9.'),
  );

export const pincodeSchema = z.string().trim().regex(PINCODE_PATTERN, 'Enter a 6-digit PIN code.');

export const otpCodeSchema = z
  .string()
  .trim()
  .regex(/^\d{6}$/, 'Enter the 6-digit code we sent you.');

export const otpFormSchema = z.object({
  phone: phoneSchema,
});

export const otpVerifySchema = z.object({
  code: otpCodeSchema,
});

/**
 * The name, asked for on its own screen AFTER a verify that came back without
 * one. Required, unlike the optional field it replaces: we now only ask when
 * the account genuinely has no name, so there is nothing to fall back on.
 */
export const profileNameSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Enter the name we should print on your parcel.')
    .max(120, 'That name is too long.'),
});

export const addressSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Enter the name the parcel should be addressed to.')
    .max(120, 'That name is too long.'),
  phone: phoneSchema,
  line1: z
    .string()
    .trim()
    .min(3, 'Enter the flat or house number and the street.')
    .max(180, 'That is longer than we can print on a label.'),
  /**
   * Area AND landmark, in one box.
   *
   * They were two inputs and Indian addresses do not separate them: people
   * write "Panampilly Nagar, near the Bharat petrol pump" as one thought, and
   * asked for it in two boxes they either split it arbitrarily or leave one
   * empty. The `landmark` COLUMN still exists — it is frozen into order
   * snapshots and printed on invoices — it is just no longer collected apart
   * from the rest of the line.
   */
  line2: z.string().trim().max(180, 'That is longer than we can print on a label.').optional(),
  city: z.string().trim().min(2, 'Enter your city or town.').max(80, 'That city name is too long.'),
  state: z.string().trim().min(2, 'Enter your state.').max(80, 'That state name is too long.'),
  pincode: pincodeSchema,
});

export type AddressFormValues = z.infer<typeof addressSchema>;
export type OtpFormValues = z.infer<typeof otpFormSchema>;
export type OtpVerifyValues = z.infer<typeof otpVerifySchema>;
export type ProfileNameValues = z.infer<typeof profileNameSchema>;
